import { MetalsTaskType } from '../enums/metals-task-type.enum';
import {
    MetalsAssemblyQuoteBoard,
    MetalsAssemblyQuoteMaterial,
    MetalsAssemblyQuoteRates,
    MetalsAssemblyQuoteStockForm,
    MetalsAssemblyQuoteStockProfile,
} from '../models/metals-assembly-quote.model';

/** the operations a part can be sent through, in the order the picker offers them */
export const METALS_ASSEMBLY_QUOTE_OPERATION_TYPES: readonly MetalsTaskType[] = [
    MetalsTaskType.Laser,
    MetalsTaskType.TurretPunch,
    MetalsTaskType.Stamping,
    MetalsTaskType.PressBrake,
    MetalsTaskType.RollForming,
    MetalsTaskType.DrillPress,
    MetalsTaskType.Deburring,
    MetalsTaskType.PEMs,
    MetalsTaskType.Fabricating,
    MetalsTaskType.PowderCoating,
    MetalsTaskType.OutsideProcessing,
    MetalsTaskType.Assembly,
    MetalsTaskType.Packaging,
];

/** how each operation reads on a card, a chip or a rates row */
export const METALS_ASSEMBLY_QUOTE_OPERATION_LABELS: Record<MetalsTaskType, string> = {
    [MetalsTaskType.Laser]: 'Laser',
    [MetalsTaskType.TurretPunch]: 'Turret punch',
    [MetalsTaskType.Stamping]: 'Stamping',
    [MetalsTaskType.PressBrake]: 'Press brake',
    [MetalsTaskType.RollForming]: 'Roll forming',
    [MetalsTaskType.DrillPress]: 'Drill press',
    [MetalsTaskType.Deburring]: 'Deburring',
    [MetalsTaskType.PEMs]: 'PEMs',
    [MetalsTaskType.Fabricating]: 'Fabrication',
    [MetalsTaskType.PowderCoating]: 'Powder coating',
    [MetalsTaskType.OutsideProcessing]: 'Outside processing',
    [MetalsTaskType.Assembly]: 'Assembly',
    [MetalsTaskType.Packaging]: 'Packaging',
    [MetalsTaskType.ToolRoom]: 'Tool room',
    [MetalsTaskType.Transfer]: 'Transfer',
};

/** priced as a flat $ per part rather than shop minutes */
export function metalsAssemblyQuoteOperationIsFlatCost(type: MetalsTaskType): boolean {
    return type === MetalsTaskType.OutsideProcessing;
}

export const METALS_ASSEMBLY_QUOTE_MATERIAL_FAMILIES: readonly string[] = ['Mild', 'Stainless', 'Galvanized', 'Aluminum'];

/** lb per cubic inch, by family - what turns dimensions into a weight */
export const METALS_ASSEMBLY_QUOTE_DENSITY_LB_PER_CUBIC_INCH: Record<string, number> = {
    Mild: 0.2836,
    Stainless: 0.289,
    Galvanized: 0.2836,
    Aluminum: 0.0975,
};

export const METALS_ASSEMBLY_QUOTE_STOCK_FORMS: readonly { form: MetalsAssemblyQuoteStockForm; label: string; note: string }[] = [
    { form: 'sheet', label: 'Sheet / plate', note: 'a flat blank: thickness x length x width' },
    { form: 'tube', label: 'Tube', note: 'a cut of round, square or rectangular tube' },
    { form: 'bar', label: 'Bar / billet', note: 'a cut of solid round or rectangular stock' },
    { form: 'weight', label: 'By weight', note: 'you already know the pounds' },
];

export const METALS_ASSEMBLY_QUOTE_STOCK_PROFILES: readonly { profile: MetalsAssemblyQuoteStockProfile; label: string }[] = [
    { profile: 'round', label: 'Round' },
    { profile: 'square', label: 'Square' },
    { profile: 'rectangular', label: 'Rectangular' },
];

/** common sheet and plate thicknesses, steel gauge table - quick picks under the thickness field */
export const METALS_ASSEMBLY_QUOTE_THICKNESS_PRESETS: readonly { label: string; inches: number }[] = [
    { label: '22 ga', inches: 0.0299 },
    { label: '20 ga', inches: 0.0359 },
    { label: '18 ga', inches: 0.0478 },
    { label: '16 ga', inches: 0.0598 },
    { label: '14 ga', inches: 0.0747 },
    { label: '12 ga', inches: 0.1046 },
    { label: '11 ga', inches: 0.1196 },
    { label: '10 ga', inches: 0.1345 },
    { label: '7 ga', inches: 0.1793 },
    { label: '1/4"', inches: 0.25 },
    { label: '3/8"', inches: 0.375 },
    { label: '1/2"', inches: 0.5 },
];

export function emptyMetalsAssemblyQuoteMaterial(): MetalsAssemblyQuoteMaterial {
    return { family: null, form: 'sheet', profile: 'round', thicknessIn: 0, lengthIn: 0, widthIn: 0, sizeIn: 0, weightLb: 0 };
}

/**
 * Starting rates. PLACEHOLDERS: the $/hr figures are round numbers to make the
 * prototype price something, not the shop's rates. $/lb matches the DXF quoting
 * tool's defaults. All of it is editable on the board until a rates page exists.
 */
export const DEFAULT_METALS_ASSEMBLY_QUOTE_RATES: MetalsAssemblyQuoteRates = {
    hourlyRateByTaskType: {
        [MetalsTaskType.Laser]: 150,
        [MetalsTaskType.TurretPunch]: 120,
        [MetalsTaskType.Stamping]: 90,
        [MetalsTaskType.PressBrake]: 90,
        [MetalsTaskType.RollForming]: 90,
        [MetalsTaskType.DrillPress]: 60,
        [MetalsTaskType.Deburring]: 60,
        [MetalsTaskType.PEMs]: 60,
        [MetalsTaskType.Fabricating]: 85,
        [MetalsTaskType.PowderCoating]: 75,
        [MetalsTaskType.OutsideProcessing]: 0,
        [MetalsTaskType.Assembly]: 60,
        [MetalsTaskType.Packaging]: 50,
        [MetalsTaskType.ToolRoom]: 90,
        [MetalsTaskType.Transfer]: 0,
    },
    pricePerLbByFamily: {
        Mild: 0.79,
        Stainless: 2.10,
        Galvanized: 0.84,
        Aluminum: 4.43,
    },
};

export const METALS_ASSEMBLY_QUOTE_DEFAULT_QUANTITY = 10;
export const METALS_ASSEMBLY_QUOTE_DEFAULT_MARGIN_PERCENT = 30;

export function emptyMetalsAssemblyQuoteBoard(): MetalsAssemblyQuoteBoard {
    return {
        name: 'Untitled assembly',
        quantity: METALS_ASSEMBLY_QUOTE_DEFAULT_QUANTITY,
        marginPercent: METALS_ASSEMBLY_QUOTE_DEFAULT_MARGIN_PERCENT,
        stepFileName: null,
        nodes: [],
        links: [],
        rates: {
            hourlyRateByTaskType: { ...DEFAULT_METALS_ASSEMBLY_QUOTE_RATES.hourlyRateByTaskType },
            pricePerLbByFamily: { ...DEFAULT_METALS_ASSEMBLY_QUOTE_RATES.pricePerLbByFamily },
        },
    };
}
